// closing-workflows-table.component.ts
import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { HamButtonComponent } from '../../../../aumico-components/src/lib';

import { TasksOverviewActions } from '../../../../aumico-lib-cpmm-fe/src/lib';
import { SortDirection } from '../../../../aumico-lib-cpmm-fe/src/lib/enums/sort-direction.enum';
import { TaskState } from '../../../../aumico-lib-cpmm-fe/src/lib/enums/task-state.enum';
import { WorkflowArea } from '../../../../aumico-lib-cpmm-fe/src/lib/enums/workflow-area.enum';
import { activeNavigation } from '../enums/activeNavigation.enum';
import { ProjectSortingProperties } from '../enums/project-sorting-properties.enum';
import { ProjectStatus } from '../enums/project-status.enum';
import { IssueDetailTableValue } from '../interfaces/issue-detail-table-value.interface';
import { ProjectOverviewTableValue } from '../interfaces/project-overview-table-value.interface';

/**
 * The single column that stands for the ABCP closing in place of the workflow sections — a sentinel
 * summary under the closing-process group, so headers, column classes and widths need no second path.
 */
export const ABCP_CLOSING_SUMMARY = 'abcp_closing';

@Component({
    selector: 'aumico-project-overview-table',
    templateUrl: './project-overview-table.component.html',
    styleUrls: ['./project-overview-table.component.scss'],
})
export class ProjectOverviewTableComponent implements OnInit, OnChanges, AfterViewChecked {
    @Input() projects: ProjectOverviewTableValue[] = [];
    @Input() activeNav: activeNavigation = activeNavigation.ALL_PROJECTS;
    @Input() anyActiveFilter = false;
    @Input() activeSortBy: ProjectSortingProperties | null = null;
    @Input() activeSortOrder: SortDirection | null = null;
    @Input() disableNewProjectButtonEmptyState: boolean = false;
    @Input() allProjectsSelected: boolean = false;
    @Input() selectedProjects: Set<string> = new Set();
    /** REV-22155 — column "Tag", only when the organization has tags. */
    @Input() showTags = false;
    /**
     * ABCP organisations work in Jahresabschluss, not in the closing workflow: the workflow's section
     * columns give way to one column that says whether the closing was started.
     */
    @Input() abcpClosing = false;
    @Output() toggleCheckAllProject = new EventEmitter<boolean>();
    @Output() toggleCheckProject = new EventEmitter<string>();


    public navEnum = activeNavigation;

    groupedAreas: { area: WorkflowArea; summaries: string[] }[] = [];
    columnClasses: string[] = [];

    ProjectSortingProperties = ProjectSortingProperties;

    openedActionFlyoutProjectId: string | null = null;

    openProjectDeletionConfirmationDialog = false;

    actionFlyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };
    issueDetailsFlyoutPosition: { top: number; left: number; isAbove?: boolean } = { top: 0, left: 0, isAbove: false };
    hasCapturedIssueDetailsFlyoutPosition = false;

    hovered = false;
    hoveredProjectId: string | null = null;
    hoveredSummary: string | null = null;

    private flyoutCloseTimer: any;

    // Flyout positioning constants
    private readonly DEFAULT_FLYOUT_HEIGHT = 390; // From the max-height in css
    private readonly DEFAULT_FLYOUT_WIDTH = 224;
    private readonly VIEWPORT_PADDING = 10; // Padding from viewport edges

    ProjectStatus = ProjectStatus;

    TaskState = TaskState;

    @Output() sortProjects = new EventEmitter<{
        sortBy: ProjectSortingProperties | null;
        sortOrder: SortDirection | null;
    }>();

    @Output() updateProjectStatus = new EventEmitter<{
        projectId: string;
        status: ProjectStatus;
        updateTasks: boolean;
    }>();

    @Output() deleteProject = new EventEmitter<string>();

    @Output() selectProject = new EventEmitter<{
        project: ProjectOverviewTableValue;
        area?: WorkflowArea | 'report' | 'closing';
        section?: string;
        sectionClosingProcess?: string;
    }>();

    // Empty Page: Buttons
    @Output() goToCompanies = new EventEmitter<void>();
    @Output() addNewCompany = new EventEmitter<void>();
    @Output() addNewProject = new EventEmitter<void>();

    totalTableLength = 0;

    // Parent project expand/collapse state
    expandedParentProjects = new Map<string, boolean>();

    // Filtered projects that respect parent/child hierarchy and expand/collapse states
    filteredProjects: ProjectOverviewTableValue[] = [];

    @ViewChild('flyout') flyout: ElementRef;
    @ViewChild('issueListContainer') issueListContainer: ElementRef;
    @ViewChild('issueList') issueList: ElementRef;
    @ViewChild('issueDetailsFlyout', { read: ElementRef }) issueDetailsFlyoutRef: ElementRef;

    isIssueListScrollable = false;

    openAutoTaskUpdateDialog: boolean = false;
    projectIdToBeChanged: string;

    // Shift+click range selection support
    private lastSelectedProjectIndex: number = -1;

    constructor(private cdr: ChangeDetectorRef, private zone: NgZone, private store: Store) { }

    ngOnInit(): void { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['projects']?.currentValue != null) {
            this.initializeParentProjectStates();
            this.updateFilteredProjects();
            this.groupedAreas = this.getGroupedWorkflowAreas(this.filteredProjects);
            // Reset shift+click state when projects change
            this.lastSelectedProjectIndex = -1;
        } else if (changes['abcpClosing']) {
            this.groupedAreas = this.getGroupedWorkflowAreas(this.filteredProjects);
        }
        this.columnClasses = [
            'stick-0', // company
            'middle-cols', // duedate
            'middle-cols', // assignee
            'middle-cols', // reportType
            'middle-cols', // status
            ...(this.showTags ? ['middle-cols'] : []), // tag
            // Map dynamic columns to 'middle-cols-MM' or 'middle-cols-CP' depending on the WorkflowArea
            ...this.groupedAreas.flatMap((g) =>
                g.summaries.map(() => `middle-cols-${g.area === WorkflowArea.MANDATE_MANAGEMENT ? 'MM' : 'CP'}`),
            ), // dynamic columns per group (MM / CP)
            'report', // report
            'sticky-action', // actions
        ];

        // We want a fixed Table layout, but the total width should depend on the amount of cols. 800 is for the standard configuration
        // Increased base width from 735 to 1035 to give more space to the first column (company name + checkbox)
        this.totalTableLength =
            1035 +
            (this.showTags ? 150 : 0) +
            this.groupedAreas.reduce((sum, group) => sum + group.summaries.length, 0) * 220;

        if (this.activeNav === activeNavigation.MY_PROJECTS) {
            this.columnClasses = this.columnClasses.filter((_, idx) => idx !== 2); // Remove assignee column for MY_PROJECTS
        }
    }

    getGroupedWorkflowAreas(data: ProjectOverviewTableValue[]) {
        const areaMap = new Map<WorkflowArea, Set<string>>();
        data.forEach((entry) => {
            entry.workflowData?.forEach((wf) => {
                if (!areaMap.has(wf.area)) areaMap.set(wf.area, new Set());
                areaMap.get(wf.area)?.add(wf.summary);
            });
        });

        // Reorder areaMap so that 'MANDATE_MANAGEMENT' is always first
        const reorderedAreaMap = new Map<WorkflowArea, Set<string>>();
        if (areaMap.has(WorkflowArea.MANDATE_MANAGEMENT)) {
            reorderedAreaMap.set(WorkflowArea.MANDATE_MANAGEMENT, areaMap.get(WorkflowArea.MANDATE_MANAGEMENT)!);
        }
        // Add all other entries
        areaMap.forEach((value, key) => {
            if (key !== WorkflowArea.MANDATE_MANAGEMENT) {
                reorderedAreaMap.set(key, value);
            }
        });

        if (this.abcpClosing) {
            reorderedAreaMap.set(WorkflowArea.CLOSING_PROCESS, new Set([ABCP_CLOSING_SUMMARY]));
        }

        return Array.from(reorderedAreaMap.entries()).map(([area, summaries]) => ({
            area,
            summaries: Array.from(summaries),
        }));
    }

    isAbcpClosingColumn(area: WorkflowArea, summary: string): boolean {
        return area === WorkflowArea.CLOSING_PROCESS && summary === ABCP_CLOSING_SUMMARY;
    }

    getSummaryBadge(element: ProjectOverviewTableValue, area: WorkflowArea, summary: string): string {
        const entry = element.workflowData?.find((w) => w.area === area && w.summary === summary);
        if (entry && entry.completedIssueCount == 0 && entry.totalIssueCount == 0) {
            return 'N/A';
        }
        return entry ? `${entry.completedIssueCount}/${entry.totalIssueCount}` : '-';
    }

    getSummaryTooltipIssues(): IssueDetailTableValue[] {
        const project = this.projects.find((project) => project.projectId === this.hoveredProjectId);

        const entry = project?.workflowData?.find((w) => w.summary === this.hoveredSummary);
        return entry
            ? entry.issuesDetails.filter((issue: IssueDetailTableValue) => issue.state > TaskState.NOT_APPLICABLE)
            : [];
    }

    onRowSelected(row: ProjectOverviewTableValue): void {
        //this.selectProject.emit(row);
    }

    onUpdateStatus(projectId: string, status: ProjectStatus, taskUpdate: boolean): void {
        this.openAutoTaskUpdateDialog = false;
        this.updateProjectStatus.emit({ projectId, status, updateTasks: taskUpdate });
    }

    onOpenAutoTaskUpdateDialog(projectId: string): void {
        this.openAutoTaskUpdateDialog = true;
        this.projectIdToBeChanged = projectId;
    }

    onDeleteProject(projectId: string): void {
        this.deleteProject.emit(projectId);
        this.openProjectDeletionConfirmationDialog = false;
    }

    sortBy(sortBy: ProjectSortingProperties): void {
        // If we're sorting by the same column as the current active sort
        if (this.activeSortBy === sortBy) {
            if (this.activeSortOrder === SortDirection.ASC) {
                this.sortProjects.emit({ sortBy, sortOrder: SortDirection.DESC });
            } else {
                this.sortProjects.emit({ sortBy: null, sortOrder: null });
        }
        } else {
            // New column, start with ASC
            this.sortProjects.emit({ sortBy, sortOrder: SortDirection.ASC });
        }
    }

    public openActionFlyout(projectId: string, buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();

        // Simple check: if flyout doesn't fit below, move it up by 100px
        const flyoutHeight = 90; // Approximate flyout height
        const spaceBelow = window.innerHeight - rect.bottom;
        const top = spaceBelow < flyoutHeight ? rect.bottom - 100 : rect.bottom;

        this.actionFlyoutPosition = {
            top: top,
            right: rect.right,
            left: rect.left - 200,
        };

        this.openedActionFlyoutProjectId = projectId;
    }

    closeActionFlyout(): void {
        this.openedActionFlyoutProjectId = null;
    }

    startHover(event: MouseEvent, projectId: string, summary: string): void {
        if (this.flyoutCloseTimer) {
            clearTimeout(this.flyoutCloseTimer);
        }
        this.hasCapturedIssueDetailsFlyoutPosition = false;
        this.setIssueDetailsFlyoutPosition(event);
        this.hoveredProjectId = projectId;
        this.hoveredSummary = summary;
    }

    endHover(): void {
        this.flyoutCloseTimer = setTimeout(() => {
            this.hovered = false;
            this.hasCapturedIssueDetailsFlyoutPosition = false;
            this.hoveredProjectId = null;
            this.hoveredSummary = null;
        }, 500);
    }

    keepFlyoutOpen(): void {
        if (this.flyoutCloseTimer) {
            clearTimeout(this.flyoutCloseTimer);
        }
    }

    setIssueDetailsFlyoutPosition(evt: MouseEvent): void {
        if (this.hasCapturedIssueDetailsFlyoutPosition) {
            return;
        }

        const hostEl = evt.currentTarget as HTMLElement;
        const rect = hostEl.getBoundingClientRect();

        const position = this.calculateFlyoutPosition(rect);

        this.issueDetailsFlyoutPosition = {
            top: position.top,
            left: position.left,
            isAbove: position.isAbove,
        };

        this.hasCapturedIssueDetailsFlyoutPosition = true;
        this.hovered = true;

        this.repositionFlyoutAfterRender('.issue-details-flyout', rect);
    }

    private calculateFlyoutPosition(
        buttonRect: DOMRect,
        flyoutElement?: HTMLElement,
        flyoutWidth: number = this.DEFAULT_FLYOUT_WIDTH,
        flyoutHeight: number = this.DEFAULT_FLYOUT_HEIGHT,
    ): { top: number; left: number; isAbove: boolean } {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Use actual flyout dimensions if available, otherwise use defaults
        const actualWidth = flyoutElement ? flyoutElement.offsetWidth : flyoutWidth;
        const actualHeight = flyoutElement ? flyoutElement.offsetHeight : flyoutHeight;
        // Calculate if flyout fits below the button
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const fitsBelow = spaceBelow >= actualHeight + this.VIEWPORT_PADDING;
        const fitsAbove = spaceAbove >= actualHeight + this.VIEWPORT_PADDING;

        // Determine vertical position
        let top: number;
        let isAbove = false;

        if (fitsBelow) {
            // Position below button
            top = buttonRect.bottom;
        } else if (fitsAbove) {
            // Position above button
            top = buttonRect.top - actualHeight;
            isAbove = true;
        } else {
            // If it doesn't fit either way, position where there's more space
            if (spaceBelow > spaceAbove) {
                top = buttonRect.bottom;
            } else {
                // When positioning above, the bottom of the flyout should align with the top of the button.
                top = buttonRect.top - actualHeight;
                isAbove = true;
            }
        }

        // Calculate horizontal position
        const centerX = buttonRect.left + buttonRect.width / 2;
        let left = centerX - actualWidth / 2;

        // Check if flyout extends beyond right edge of viewport
        if (left + actualWidth > viewportWidth - this.VIEWPORT_PADDING) {
            left = viewportWidth - this.VIEWPORT_PADDING - actualWidth;
        }

        // Ensure flyout doesn't extend beyond left edge
        if (left < this.VIEWPORT_PADDING) {
            left = this.VIEWPORT_PADDING;
        }

        return { top, left, isAbove };
    }

    private repositionFlyoutAfterRender(flyoutSelector: string, buttonRect: DOMRect): void {
        this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.zone.run(() => {
                        const flyoutElement = this.issueDetailsFlyoutRef?.nativeElement as HTMLElement;
                        if (flyoutElement) {
                            const position = this.calculateFlyoutPosition(buttonRect, flyoutElement);
                            this.issueDetailsFlyoutPosition = position;
                            this.cdr.detectChanges(); // Trigger change detection to apply the new position
                        }
                    });
                });
            });
        });
    }

    ngAfterViewChecked() {
        this.checkIfIssueListIsScrollable();
    }

    private checkIfIssueListIsScrollable() {
        if (this.issueListContainer && this.issueList) {
            const isScrollable =
                this.issueList.nativeElement.scrollHeight > this.issueListContainer.nativeElement.clientHeight;
            if (isScrollable !== this.isIssueListScrollable) {
                this.isIssueListScrollable = isScrollable;
                this.cdr.detectChanges();
            }
        }
    }

    // Empty Page: Buttons
    onGoToCompanies(): void {
        this.goToCompanies.emit();
    }

    onAddNewCompany(): void {
        this.addNewCompany.emit();
    }

    onAddNewProject(): void {
        this.addNewProject.emit();
    }

    private initializeParentProjectStates(): void {
        // Initialize expanded state for all parent projects (expanded by default)
        this.projects.forEach((project) => {
            if (project.isParentProject) {
                if (!this.expandedParentProjects.has(project.projectId)) {
                    this.expandedParentProjects.set(project.projectId, true);
                }
            }
        });
    }

    private updateFilteredProjects(): void {
        this.filteredProjects = [];

        this.projects.forEach((project) => {
            // Always show parent projects
            if (project.isParentProject) {
                this.filteredProjects.push(project);
            }
            // Show child projects only if their parent is expanded
            else if (project.parentProjectId) {
                const isParentExpanded = this.expandedParentProjects.get(project.parentProjectId);
                if (isParentExpanded) {
                    this.filteredProjects.push(project);
                }
            }
            // Show projects that are neither parent nor child
            else {
                this.filteredProjects.push(project);
            }
        });
    }

    toggleParentProject(projectId: string): void {
        const currentState = this.expandedParentProjects.get(projectId) || false;
        this.expandedParentProjects.set(projectId, !currentState);
        this.updateFilteredProjects();
        this.groupedAreas = this.getGroupedWorkflowAreas(this.filteredProjects);
    }

    isParentExpanded(projectId: string): boolean {
        return this.expandedParentProjects.get(projectId) || false;
    }

    // Badges for CPMM
    isOverdue(dueDate: string | Date): boolean {
        return new Date(dueDate) < new Date();
    }

    isDueSoon(dueDate: string | Date): boolean {
        const today = new Date();
        const due = new Date(dueDate);

        const timeDiff = due.getTime() - today.getTime();
        const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

        return daysDiff >= 0 && daysDiff <= 7;
    }

    taskStatus(element: ProjectOverviewTableValue, area: WorkflowArea, summary: string): string {
        const entry = element.workflowData?.find((w) => w.area === area && w.summary === summary);
        if (!entry) {
            return '';
        }
        if (entry.completedIssueCount === entry.totalIssueCount) {
            if (entry.completedIssueCount == 0) {
                return 'N/A';
            }
            return 'done';
        } else if (area === 'CLOSING_PROCESS') {
            return '';
        }
        let highestDate;
        for (const issue of entry?.issuesDetails) {
            if (issue.state === TaskState.DONE || issue.state === TaskState.NOT_APPLICABLE) {
                continue;
            }
            if (issue.dueDate) {
                if (this.isOverdue(issue.dueDate)) {
                    return 'overdue';
                }
                if (!highestDate || new Date(issue.dueDate) < new Date(highestDate)) {
                    highestDate = issue.dueDate;
                }
            }
        }
        if (highestDate) {
            const timeDiff = new Date(highestDate).getTime() - new Date().getTime();
            const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
            if (daysDiff >= 0 && daysDiff <= 7) {
                return 'almostOverdue';
            }
        }
        return '';
    }

    onSummaryClick(projectId: string, area: WorkflowArea, summary: string, sectionClosingProcess?: string): void {
        const project = this.projects.find((project) => project.projectId === projectId);

        // Navigate using relative navigation with injected router and activatedRoute
        if (this.isAbcpClosingColumn(area, summary)) {
            this.selectProject.emit({ project, area: 'closing' });
        } else if (area === WorkflowArea.MANDATE_MANAGEMENT) {
            this.selectProject.emit({ project, area, section: summary });
        } else if (area === WorkflowArea.CLOSING_PROCESS) {
            this.selectProject.emit({ project, area, section: summary, sectionClosingProcess });
        }
    }

    onGeneralDataClicked(projectId: string): void {
        const project = this.projects.find((project) => project.projectId === projectId);

        // Navigate to the project general page using relative navigation
        this.selectProject.emit({ project });
    }

    onReportCellClicked(projectId: string): void {
        const project = this.projects.find((project) => project.projectId === projectId);

        // Navigate using relative navigation
        this.selectProject.emit({ project, area: 'report' });
    }

    changeItemState(
        projectId: string,
        area: WorkflowArea,
        summary: string,
        itemName: string,
        issueId: string,
        taskState: TaskState,
    ): void {
        if (area === WorkflowArea.MANDATE_MANAGEMENT) {
            this.store.dispatch(
                TasksOverviewActions.updateTaskStatusForProjectByTaskId({
                    projectId,
                    taskId: issueId,
                    updateTaskDto: { taskState },
                }),
            );
        } else if (area === WorkflowArea.CLOSING_PROCESS) {
            this.store.dispatch(
                TasksOverviewActions.updateAllTasksStatusForProjectBySectionId({
                    projectId,
                    sectionId: issueId,
                    updateTaskDto: { taskState },
                }),
            );
        }
    }

    private handleShiftClick(projectId: string): void {
        const currentIndex = this.filteredProjects.findIndex(p => p.projectId === projectId);

        if (this.lastSelectedProjectIndex === -1 || currentIndex === -1) {
            // No previous selection or current item not found, treat as normal click
            this.handleNormalClick(projectId, currentIndex);
            return;
        }

        // Determine range bounds
        const startIndex = Math.min(this.lastSelectedProjectIndex, currentIndex);
        const endIndex = Math.max(this.lastSelectedProjectIndex, currentIndex);

        // Determine the target state based on the shift+clicked project's current state
        const isCurrentlySelected = this.selectedProjects.has(projectId);
        const targetState = !isCurrentlySelected; // If unselected, target is selected; if selected, target is unselected

        // Only emit projects that need to change to match the target state
        for (let i = startIndex; i <= endIndex; i++) {
            const project = this.filteredProjects[i];
            if (project) {
                const projectCurrentlySelected = this.selectedProjects.has(project.projectId);

                // Only emit if the project's current state differs from target state
                if (projectCurrentlySelected !== targetState) {
                    this.toggleCheckProject.emit(project.projectId);
                }
            }
        }

        // Update last selected index to the clicked project
        this.lastSelectedProjectIndex = currentIndex;
    }

    private handleNormalClick(projectId: string, currentIndex: number = -1): void {
        if (currentIndex === -1) {
            currentIndex = this.filteredProjects.findIndex(p => p.projectId === projectId);
        }

        this.lastSelectedProjectIndex = currentIndex;
        this.toggleCheckProject.emit(projectId);
    }

    onProjectCheckboxClick(projectId: string, event: MouseEvent): void {
        event.stopPropagation();

        if (event.shiftKey) {
            this.handleShiftClick(projectId);
        } else {
            this.handleNormalClick(projectId);
        }
    }

    onToggleCheckAllProject(): void {
        this.toggleCheckAllProject.emit(!this.allProjectsSelected);
    }

    onToggleCheckProject(projectId: string): void {
        this.toggleCheckProject.emit(projectId);
    }
}
